import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LogOut, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

function initials(name, email) {
    const src = (name || email || "").trim();
    if (!src) return "?";
    const parts = src.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
}

export default function UserMenu() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    // Phase 8M.1 — sign-out confirmation modal.
    const [confirmLogout, setConfirmLogout] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (!ref.current || !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    if (!user) {
        return (
            <Link
                to="/login"
                data-testid="signin-link"
                className="text-[13px] text-[#0A0A0A] underline underline-offset-4 transition hover:text-[#737373]"
            >
                Sign in
            </Link>
        );
    }

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="user-avatar"
                aria-label="Account menu"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-[11px] font-semibold tracking-tight text-[#0A0A0A] transition hover:border-[#0A0A0A]"
            >
                {initials(user.name, user.email)}
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="user-menu"
                    className="absolute right-0 mt-2 w-60 overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.06)]"
                >
                    <div className="px-4 py-3">
                        <div
                            className="text-[13px] font-medium text-[#0A0A0A]"
                            data-testid="user-menu-name"
                        >
                            {user.name || user.email.split("@")[0]}
                        </div>
                        <div
                            className="truncate text-[12px] text-[#737373]"
                            data-testid="user-menu-email"
                        >
                            {user.email}
                        </div>
                    </div>
                    <div className="border-t border-[#E5E5E5]">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                setConfirmLogout(true);
                            }}
                            data-testid="user-menu-logout"
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-[13px] text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                        >
                            <LogOut size={13} strokeWidth={2} />
                            Sign out
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                setDeleteOpen(true);
                            }}
                            data-testid="delete-account-button"
                            className="group flex w-full items-center gap-2 border-t border-[#E5E5E5] px-4 py-2.5 text-[13px] text-[#0A0A0A] transition hover:bg-[#F5F5F5] hover:text-[#B91C1C]"
                        >
                            <Trash2
                                size={13}
                                strokeWidth={2}
                                className="text-[#0A0A0A] transition group-hover:text-[#B91C1C]"
                            />
                            Delete account
                        </button>
                    </div>
                </div>
            )}
            <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />

            {/* Phase 8M.1 — sign-out confirmation modal */}
            {confirmLogout && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={() => setConfirmLogout(false)}
                    data-testid="logout-confirm-modal"
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-[0_12px_32px_rgba(0,0,0,0.18)]"
                    >
                        <h2 className="text-[16px] font-semibold text-[#0A0A0A]">
                            {t("auth.logout_confirm_title")}
                        </h2>
                        <p className="mt-2 text-[13px] leading-snug text-[#525252]">
                            {t("auth.logout_confirm_message")}
                        </p>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmLogout(false)}
                                data-testid="logout-cancel-button"
                                className="rounded-lg bg-[#F5F5F5] px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#E5E5E5]"
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    setConfirmLogout(false);
                                    await logout();
                                }}
                                data-testid="logout-confirm-button"
                                className="rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-semibold text-white transition hover:bg-[#262626]"
                            >
                                {t("auth.logout_confirm_button")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
